import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthToken, MapAuthToken, DashboardAuthToken, ExtenalLinkAuthToken, HorizonRedirect } from 'src/app/modules/core/store/models/auth-token.model';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { AuthConnection } from './auth-connection.model';
import { Store } from '@ngrx/store';
import * as fromAuth from './';
import { UserAuthKeyExpiration } from './auth-key-expired.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {
  private count = 0;

  constructor(private http: HttpService, public store: Store<fromAuth.State>) {}

  public getAnonymousToken(): Observable<AuthToken> {
    const url = `${apiBaseUrl}/v1/iam/auth/anonymous-token`;

    return this.http.get<AuthToken>(url);
  }

  public invalidateToken(): Observable<void> {
    const url = `${apiBaseUrl}/v1/iam/auth/token`;

    return this.http.get(url);
  }

  public getConnection(username: string): Observable<AuthConnection> {
    const queryString = this.http.queryStringFromObject({ email: username });

    const url = `${apiBaseUrl}/v1/iam/auth-connections/for-user-email?${queryString}`;

    return this.http.get<AuthConnection>(url, { email: username });
  }

  public getAuthenticatedToken(code: string, state: string): Observable<AuthToken> {
    const queryString = this.http.queryStringFromObject({ code, state });

    const url = `${apiBaseUrl}/v1/iam/auth/authenticated-token?${queryString}`;

    return this.http.get<AuthToken>(url);
  }

  public getAuthenticatedTokenByOrganization(organizationUId: string): Observable<AuthToken> {
    const queryString = this.http.queryStringFromObject({ organizationUId });

    const url = `${apiBaseUrl}/v1/iam/auth/change-organization?${queryString}`;

    return this.http.get<AuthToken>(url);
  }

  public authenticateUsernamePassword(username: string, password: string, cultureCode: string): Observable<AuthToken> {
    const url = `${apiBaseUrl}/v1/iam/auth/authenticate`;

    return this.http.post<AuthToken>(url, { username, password, cultureCode });
  }

  public getTokenForHorizonAuthentication(jwt: string, orgId: string): Observable<AuthToken> {
    const queryString = this.http.queryStringFromObject({ jwt, orgId });

    const url = `${apiBaseUrl}/v1/iam/horizon-auth/horizon-token?${queryString}`;

    return this.http.get<AuthToken>(url);
  }

  public getHorizonRedirect(): Observable<HorizonRedirect> {
    const url = `${apiBaseUrl}/v1/iam/horizon-auth/horizon-redirect`;
    return this.http.get<string>(url);
  }

  public logout(): Observable<void> {
    const url = `${apiBaseUrl}/v1/iam/auth/logout`;

    return this.http.get<AuthToken>(url);
  }

  public refreshAccessToken(): Observable<AuthToken> {
    let token: AuthToken;
    this.store
      .select(fromAuth.selectToken)
      .pipe(take(1))
      .subscribe(t => (token = t));

    if (!token) {
      return of(null);
    }

    const url = `${apiBaseUrl}/v1/iam/auth/refresh-token`;
    var refreshToken = token.refresh_token;

    return this.http.post<AuthToken>(url, { refreshToken });
  }

  sendPasswordResetLink(username: string) {
    const queryString = this.http.queryStringFromObject({ username });
    const url = `${apiBaseUrl}/v1/iam/auth/forgot-password?${queryString}`;

    return this.http.post(url, { username });
  }

  isInviteCodeExpired(username: string, authKey: string) {
    const queryString = this.http.queryStringFromObject({ username, authKey });
    const url = `${apiBaseUrl}/v1/iam/auth/user-invite/expiration?${queryString}`;

    return this.http.get<UserAuthKeyExpiration>(url, { username, authKey });
  }

  isResetCodeExpired(username: string, authKey: string) {
    const queryString = this.http.queryStringFromObject({ username, authKey });
    const url = `${apiBaseUrl}/v1/iam/auth/forgot-password/expiration?${queryString}`;

    return this.http.get<UserAuthKeyExpiration>(url, { username, authKey });
  }

  setUserPassword(username: string, newPassword: string, authKey: string) {
    const url = `${apiBaseUrl}/v1/iam/auth/forgot-password/reset`;

    return this.http.post(url, { username, newPassword, authKey });
  }

  resendInviteLink(username: string) {
    const queryString = this.http.queryStringFromObject({ username });
    const url = `${apiBaseUrl}/v1/iam/auth/user-invite/resend?${queryString}`;

    return this.http.post(url, { username });
  }

  setNewUserPassword(username: string, newPassword: string, authKey: string) {
    const url = `${apiBaseUrl}/v1/iam/auth/user-invite/setup`;

    return this.http.post(url, { username, newPassword, authKey });
  }

  sharedMapAuth(mapToken: string): Observable<MapAuthToken> {
    const queryString = this.http.queryStringFromObject({ mapToken });
    const url = `${apiBaseUrl}/v1/iam/auth/map-link?${queryString}`;
    return this.http.post(url);
  }

  sharedDashboardAuth(token: string): Observable<DashboardAuthToken> {
    const queryString = this.http.queryStringFromObject({ token });
    const url = `${apiBaseUrl}/v1/iam/auth/dashboard-link?${queryString}`;
    return this.http.post(url);
  }

  externalLinkAuth(token: string): Observable<ExtenalLinkAuthToken> {
    const queryString = this.http.queryStringFromObject({ token });
    const url = `${apiBaseUrl}/v1/iam/auth/external-link?${queryString}`;
    return this.http.post(url);
  }

  getZendeskSso() {
    const url = `${apiBaseUrl}/v1/iam/auth/zendesk-sso/`;
    return this.http.get(url);
  }

  getLitmosSso() {
    const url = `${apiBaseUrl}/v1/iam/auth/litmos-sso/`;
    return this.http.get(url);
  }
}
